'use client'

import { useCallback } from 'react'

import { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Hex } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgMessage } from '~/types'
import { captureException } from '~/utils/errors'
import {
  getPasskeyErrorDescription,
  startPasskeyAuthentication,
} from '~/utils/passkeys'

export type CreateMessageData = {
  chainId: SupportedChainId
  name: string
  dataToSign: Hex
  dataType: OrgMessage['dataType']
  smartAccountId: string
  walletConnectAppName?: string
  walletConnectAppIcon?: string
  walletConnectSessionRequestId?: number
  walletConnectSessionRequestTopic?: string
  onSuccess?: (message: OrgMessage) => void
}

export const useCreateMessage = (): {
  createMessage: (
    messageData: CreateMessageData,
  ) => Promise<OrgMessage | undefined>
  isProcessing: boolean
} => {
  const { isProcessing, serverPostRequest } = useServerRequests()
  const queryClient = useQueryClient()

  const createMessage = useCallback(
    async (data: CreateMessageData) => {
      const messageData = {
        smartAccountId: data.smartAccountId,
        name: data.name,
        chainId: data.chainId,
        dataToSign: data.dataToSign,
        dataType: data.dataType,
        walletConnectSessionRequestId: data.walletConnectSessionRequestId,
        walletConnectSessionRequestTopic: data.walletConnectSessionRequestTopic,
        walletConnectAppName: data.walletConnectAppName,
        walletConnectAppIcon: data.walletConnectAppIcon,
      }

      const result = await serverPostRequest<{
        message: OrgMessage
      }>({
        path: '/api/smart_accounts/messages',
        params: {
          ...messageData,
        },
        errorMessage: 'Failed to create message',
      })

      if (result) {
        const message = result.data.message
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.MESSAGES],
          predicate: (query) => {
            return (
              Array.isArray(query.queryKey) &&
              query.queryKey[1]?.smartAccountId === message.smartAccountId
            )
          },
        })

        toast.success('Message created', { id: message.id })

        data.onSuccess?.(message)
        return message
      }
    },
    [queryClient, serverPostRequest],
  )

  return {
    createMessage,
    isProcessing,
  }
}

export const useApproveMessage = (): {
  approveMessage: (
    message: OrgMessage,
    // showSuccessToast: whether to trigger a success toast or a normal one on sucessful signature verification
    // Pass true if this is the terminating step, false if it's an intermediate step
    showSuccessToast: boolean,
    onSuccess?: (message: OrgMessage) => void,
  ) => Promise<OrgMessage | undefined>
  isProcessing: boolean
} => {
  const { serverPutRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()

  const approveMessage = useCallback(
    async (
      message: OrgMessage,
      showSuccessToast: boolean,
      onSuccess?: (message: OrgMessage) => void,
    ) => {
      const options = JSON.parse(
        message.options,
      ) as PublicKeyCredentialRequestOptionsJSON

      const attResp = await startPasskeyAuthentication(options, {
        toastId: message.id,
        onError: ({ error, isNotAllowedError, isUnsupportedError }) => {
          if (isUnsupportedError) {
            toast.error('Browser does not support passkeys', {
              description:
                error instanceof Error ? error.message : String(error),
              id: message.id,
            })
            return
          }
          if (isNotAllowedError) {
            toast.dismiss(message.id)
            return
          }
          toast.error('Failed to sign message', {
            description: getPasskeyErrorDescription(error),
          })
          captureException(error, {
            tags: {
              feature: 'passkey',
              operation: 'message_signing',
              errorType: error instanceof Error ? error.name : 'unknown',
            },
            extra: {
              errorMessage:
                error instanceof Error ? error.message : String(error),
              messageId: message.id,
              smartAccountId: message.smartAccountId,
              chainId: message.chainId,
              dataType: message.dataType,
              isWebAuthnError:
                error instanceof Error && error.name?.includes('Error'),
            },
          })
        },
      })

      if (!attResp) return

      const verificationParams = {
        path: `/api/smart_accounts/messages/${message.id}/sign`,
        params: {
          authentication: JSON.stringify(attResp),
        },
      }

      const verificationResponse = await serverPutRequest<{
        verified: boolean
        message: OrgMessage
      }>({
        ...verificationParams,
        errorMessage: 'Failed to sign message',
      })

      if (verificationResponse && verificationResponse.data.verified) {
        const toastMethod = showSuccessToast ? toast.success : toast
        toastMethod(showSuccessToast ? 'Message signed' : 'Message signed...', {
          id: message.id,
        })

        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.MESSAGES],
          predicate: (query) => {
            return (
              Array.isArray(query.queryKey) &&
              query.queryKey[1]?.smartAccountId === message.smartAccountId
            )
          },
        })
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.MESSAGE, message.id],
        })
        onSuccess?.(verificationResponse.data.message)
        return verificationResponse.data.message
      }
    },
    [serverPutRequest, queryClient],
  )

  return {
    approveMessage,
    isProcessing,
  }
}
